import React, { useRef, useState, useEffect } from "react";
import AWS from "aws-sdk";
import {
  createEntityManager,
  resolveVarsFromContext,
} from "../api/EntityManager";
import { loadOntology } from "../api/OntologyUtils";
import CameraModal from "./CameraModal";

/**
 * RoomCard
 * Shows a room card (image, title, description) and lets the user update the image.
 *
 * Props:
 * - room: { id, title, description, image }  // id = "room:project_<uuid>:slug"
 * - projectId: string                         // "project_<uuid>"
 * - userId: string                            // Cognito identity id
 * - s3Prefix?: string
 * - onLocalImageChange?: (dataUrl) => void    // reflect preview in parent if desired
 * - onSaved?: (savedEntity) => void           // callback after persisting to S3/ontology
 */
export default function RoomCard({
  room,
  projectId,
  userId,
  s3Prefix,
  onLocalImageChange,
  onSaved,
}) {
  const [cameraDropdown, setCameraDropdown] = useState({ open: null });
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraModal, setCameraModal] = useState({
    open: false,
    globalId: null,
  });
  const [savingImage, setSavingImage] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const fileInputRef = useRef(null);
  const uploadGlobalIdRef = useRef(null);

  // Local mirror so the card updates instantly; parent can mirror via onLocalImageChange
  const [imagePreview, setImagePreview] = useState(room?.image || null);
  useEffect(() => setImagePreview(room?.image || null), [room?.image]);

  function openFilePicker(globalId) {
    uploadGlobalIdRef.current = globalId;
    fileInputRef.current?.click();
  }

  function openCamera(globalId) {
    setCameraDropdown({ open: null });
    setCameraModal({ open: true, globalId });
  }
  function closeCamera() {
    setCameraModal({ open: false, globalId: null });
  }

  function updateRoomImage(globalId, imageDataUrl) {
    setImagePreview(imageDataUrl);
    onLocalImageChange?.(imageDataUrl);
  }

  async function saveRoomImage(globalId, imageDataUrl) {
    setSavingImage(true);
    setSaveError(null);
    console.log("Saving saveRoomImage", globalId);
    try {
      if (!imageDataUrl) throw new Error("No image provided.");

      const roomOntology = await loadOntology("room");
      const mgr = createEntityManager(roomOntology);

      if (!AWS?.config?.credentials) {
        throw new Error("AWS.config.credentials not set");
      }
      await AWS.config.credentials.getPromise();
      const awsCreds = AWS.config.credentials;

      // Let EntityManager parse PROJECT_UUID/ROOM_ID from id pattern
      const vars = resolveVarsFromContext(mgr.meta, {
        userId, // {COGNITO_ID}
        id: globalId, // "room:project_<uuid>:slug"
        projectUuid: projectId, // optional override if needed
        s3Prefix, // optional
      });

      console.debug("Variables", vars);

      const saved = await mgr.update({
        vars,
        patch: { image: imageDataUrl },
        aws: awsCreds,
      });

      onSaved?.(saved);

      console.debug(`Saved ${saved.id}`);

      return saved;
    } catch (err) {
      console.error("[Room Save][error]", err);
      setSaveError(err?.message || String(err));
      throw err;
    } finally {
      setSavingImage(false);
    }
  }

  function onFilePicked(e) {
    const globalId = uploadGlobalIdRef.current;
    const file = e.target.files?.[0];
    if (!file || !globalId) {
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      updateRoomImage(globalId, dataUrl);
      try {
        await saveRoomImage(globalId, dataUrl);
      } catch {
        /* error shown via saveError */
      }
    };
    reader.onerror = () => console.error("File read failed");
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  if (!room) return null;

  return (
    <div className="room-card-in-chat" aria-label="RoomCard">
      {/* hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onFilePicked}
      />

      <div className="room-project-layout">
        <div className="room-project-top-row">
          <div className="room-card-thumbnail">
            <div className="room-card-box">
              <div className="room-card-image-container relative">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt={room.title}
                    className="room-card-image"
                  />
                ) : (
                  <div className="room-card-placeholder">
                    📷 No image uploaded
                  </div>
                )}

                {savingImage && (
                  <div className="absolute top-2 left-2 text-xs bg-white/90 border rounded px-2 py-1">
                    Saving…
                  </div>
                )}
                {saveError && (
                  <div className="absolute top-2 left-2 text-xs bg-red-50 border border-red-300 rounded px-2 py-1 text-red-700">
                    Save failed
                  </div>
                )}

                <button
                  type="button"
                  className="camera-button absolute top-2 right-2 bg-white/90 border border-gray-300 rounded px-2 py-1 text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCameraDropdown({ open: room.id });
                  }}
                >
                  📷
                </button>

                {cameraDropdown.open === room.id && (
                  <div className="camera-dropdown absolute top-10 right-2 bg-white border border-gray-200 rounded shadow-md z-10">
                    <button
                      className="block w-full text-left px-3 py-2 hover:bg-gray-50"
                      onClick={() => openCamera(room.id)}
                    >
                      📸 Use Camera
                    </button>
                    <button
                      className="block w-full text-left px-3 py-2 hover:bg-gray-50"
                      onClick={() => {
                        setCameraDropdown({ open: null });
                        openFilePicker(room.id);
                      }}
                    >
                      🖼️ Upload Image
                    </button>
                  </div>
                )}
              </div>

              <div className="room-card-content">
                <h3 className="room-title">{room.title}</h3>
                <p className="room-description">{room.description || ""}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {cameraModal.open && (
        <CameraModal
          globalId={cameraModal.globalId}
          facingMode={facingMode}
          setFacingMode={setFacingMode}
          onClose={closeCamera}
          onSnap={async (dataUrl) => {
            console.log("Snapping...");
            updateRoomImage(cameraModal.globalId, dataUrl);
            try {
              await saveRoomImage(cameraModal.globalId, dataUrl);
            } catch {
              console.error("[Room Save][error]", err);
            } finally {
              // Always return to chat
              setCameraDropdown({ open: null });
              closeCamera();
            }
          }}
        />
      )}
    </div>
  );
}
